import {
  Component, OnInit, Input, Output, EventEmitter, OnDestroy, OnChanges, SimpleChanges,
  ViewChild
} from '@angular/core';
import {Conversation, Message, MessageType, ContactPermissionMessageType, ShortMessageStatus} from '../../../../models/inbox';
import {CommunicationService} from '../../../services/communication/communication.service';
import {UserService} from '../../../services/user.service';
import {Subscription} from 'rxjs/Subscription';
import {timer} from 'rxjs/observable/timer';
import {concatMap, take, filter} from 'rxjs/operators';
import {DateUtils} from '../../../utils/date.utils';
import {Router, ActivatedRoute} from '@angular/router';
import {CampaignInfluencerStatus, CommonConstant} from '../../../app.constants';
import {AchooPopupComponent} from '../../../core/achoo-popup/achoo-popup.component';
import {ModalService} from '../../../services/modal/modal.service';
import {DeviceDetectorService} from 'ngx-device-detector';
import {StringUtils} from '../../../utils/string.utils';
import {ContactPermissionStatus} from '../../../../models/influencer';
import {TranslateService} from '@ngx-translate/core';
import {InfluencerService} from '../../../services/influencer/influencer.service';
import { EventTrackerService, UserActionType, Page, ActionSource, TargetType } from '../../../services/event-tracker/event-tracker.service';
import { AppSettings } from '../../../utils/app-settings';

declare var $: any;

@Component({
  selector: 'influencer-chat',
  templateUrl: './influencer-chat.component.html',
  styleUrls: ['./influencer-chat.component.scss']
})
export class InfluencerChatComponent implements OnInit, OnDestroy, OnChanges {
  static MAX_LENGTH_MESSAGE = 1048;
  private readonly REQUEST_PERMISSION_MESSAGE_ID = -1;
  @Input()
  public conversation: Conversation;
  @Input() influencerId: number;
  @Input()
  public showActivityLog = true;

  @Output()
  public showActivityLogChange = new EventEmitter<any>();

  @Output()
  public afterReadLastMessage = new EventEmitter<any>();

  @Output()
  public newMessageAdded = new EventEmitter<any>();
  @ViewChild('AlertMessageTooLong') alertMessageTooLong: AchooPopupComponent;
  @ViewChild('ServerErrorNotification') serverErrorNotification: AchooPopupComponent;
  @ViewChild('adminEditConversationAlert') adminEditConversationAlert: AchooPopupComponent;

  public inputMessageContent = '';
  public messageList: Message[] = [];
  // server error message
  public serverErrorMessage: string;
  private pollingSubscription: Subscription;
  // last time received messages
  private lastActivity: string;
  // current page no
  private pageNo = 0;
  // this flag is using to determine whether scrolling to oldest message
  private reachLastPage = false;
  // this flag is using to determine whether user read the latest message
  private isReadLastMessage = false;
  scrollCallback;
  handleScrollDataCallback;
  private currentUserInfo: any;
  private isLandingPageMode: boolean;
  private isCommunicationPage: boolean;
  public isError = false;
  private hasAutoReplyMessage = false;

  constructor(private conversationService: CommunicationService,
              private userService: UserService,
              private modalService: ModalService,
              private deviceService: DeviceDetectorService,
              private influencerService: InfluencerService,
              private translate: TranslateService,
              private eventTracker: EventTrackerService,
              private router: Router,
              private activatedRoute: ActivatedRoute) {
    this.scrollCallback = this.onScrollUp.bind(this);
    this.handleScrollDataCallback = this.handleNextMessages.bind(this);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.conversation && this.hasChangedConvo(changes.conversation.previousValue, changes.conversation.currentValue)) {

      if (this.pollingSubscription) {
        this.pollingSubscription.unsubscribe();
      }
      this.messageList = [];
      this.pageNo = 0;
      this.reachLastPage = false;
      this.conversationService.fetchConversationDetails(this.userService.getCompanyId(), this.conversation.id, this.pageNo).subscribe((res) => {
        this.conversation.permission = res.permission;
        this.conversation.participants = res.participants;
        if (res && res.messages.length > 0) {
          this.lastActivity = res.messages[res.messages.length - 1].sentTime;
          // if component already initialized, manually added message of new convo
          if (changes.conversation.previousValue) {
            this.handleNextMessages(res.messages);
          }
        }
        this.pollingNewMessage(this.conversation);
      });
    }
  }

  ngOnInit() {
    this.userService.getCurrentUserInfo().subscribe(res => {
      if (res && res.data) {
        this.currentUserInfo = res.data;
      }
    });
    this.isCommunicationPage = this.router.url.indexOf('inbox') >= 0;
    this.activatedRoute.queryParams.subscribe((qp) => {
      this.isLandingPageMode = AppSettings.isActiveNewCampaignFlow() && (qp.accessToken || qp.inviteToken);
    });
    if (this.conversation && this.conversation.messages.length > 0) {
      this.lastActivity = this.conversation.messages[this.conversation.messages.length - 1].sentTime;
    }
  }

  ngOnDestroy() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  public pollingNewMessage(conversation) {
    // only poll when contact permission has been accepted
    if (this.userService.isInfluencer || this.isAcceptedPermission(conversation)) {
      this.pollNewMessageUntilFinish(this.userService.getCompanyId(), conversation.id);
    }
  }

  public onShowActivityLogChange(value) {
    this.showActivityLogChange.emit(value);
  }

  public isIncommingMessage(message: Message) {
    return message.type === MessageType.InComming;
  }

  public isOutgoingMessage(message: Message) {
    return message.type === MessageType.OutGoing;
  }

  public isCampaignMessage(message: Message) {
    return message.type === MessageType.Invitation;
  }

  isContactPermissionMessage(message: Message) {
    return message.messageType === ContactPermissionMessageType.accepted ||
      message.messageType === ContactPermissionMessageType.requested ||
      message.messageType === ContactPermissionMessageType.rejected;
  }

  public isShowSendButton(): boolean {
    return !!(this.inputMessageContent + '').trim().length;
  }

  public onKeyDown(evt) {
    if (this.conversation && this.conversation.isHidden) {
      return;
    }
    if (evt.keyCode === 13 && StringUtils.trim(this.inputMessageContent) !== '') {
      if (this.inputMessageContent.length > InfluencerChatComponent.MAX_LENGTH_MESSAGE) {
        this.modalService.open(this.alertMessageTooLong, null, true);
        return;
      }
      this.sendMessage(this.userService.getCompanyId(), this.conversation.id, this.inputMessageContent);
    }
  }

  public onSendMessage(messageContent?) {
    if (!messageContent) {
      if (this.inputMessageContent.length > InfluencerChatComponent.MAX_LENGTH_MESSAGE) {
        this.modalService.open(this.alertMessageTooLong, null, true);
        return;
      }
      this.sendMessage(this.userService.getCompanyId(), this.conversation.id, this.inputMessageContent);
    } else {
      this.sendMessage(this.userService.getCompanyId(), this.conversation.id, messageContent, true);
    }
  }

  public retryOnError(errorMessage) {
    if (!errorMessage) {
      return;
    }
    errorMessage.isSending = true;
    this.isError = false;
    this.sendMessage(this.userService.getCompanyId(), this.conversation.id, errorMessage.content, true);
  }

  public sendMessage(companyId, conversationId, message, isRetried?) {
    if (!isRetried) {
      this.sendMixpanel();
      this.resetChatField();
      // when user reply, it mean that they already read all new messages
      this.onReadLastMessage();
      this.scrollToBottom();
      const newMessage: Message = {
        id: 0,
        conservationId: conversationId,
        sender: {
          id: companyId,
          name: null,
          company: null,
          influencer: null,
          lastSeenMessage: null,
          user: null
        },
        content: message,
        type: MessageType.OutGoing,
        isSending: true,
        recipientName: null,
        sentTime: String(DateUtils.now().getTime())
      };
      this.messageList.push(newMessage);
    }
    this.isError = false;
    this.conversationService.sendMessage(companyId, conversationId, message).subscribe((res) => {
      if (!res || !res.message) {
        return;
      }
      if (this.userService.isInfluencer) {
        this.buildAutoReplyMessage(res.message.date);
      }
      this.afterSendingNewMessage(res.message, false);
    }, (error) => {
      this.afterSendingNewMessage(null, true);
    });
  }

  private afterSendingNewMessage(persistedMessage, isError) {
    const newMessage = this.messageList.find(msg => msg.isSending && msg.id === 0);
    if (!newMessage) {
      return;
    }
    newMessage.isSending = false;
    if (isError) {
      this.isError = true;
    } else if (persistedMessage) {
      newMessage.id = persistedMessage.id;
      newMessage.sender = persistedMessage.participant;
      newMessage.sentTime = persistedMessage.date;
    }
  }

  public onScrollUp() {
    if (!this.reachLastPage && this.conversation.id > 0) {
      return this.conversationService.fetchConversationDetails(this.userService.getCompanyId(), this.conversation.id, this.pageNo)
        .map(res => res ? res.messages : []);
    }
  }

  public handleNextMessages(messages) {
    this.messageList.splice(0, 0, ...messages);
    this.reachLastPage = Array.isArray(messages) && messages.length === 0;
    // if this is the first page, scroll to last message
    if (this.pageNo === 0) {
      this.scrollToBottom();
    }
    this.pageNo += 1;
  }

  private resetChatField() {
    this.inputMessageContent = null;
  }

  private scrollToBottom() {
    setTimeout(() => {
      const messageContainer = document.querySelector('.message-history') as HTMLElement;
      if (messageContainer) {
        const messageDomList = messageContainer.querySelectorAll('.message');
        if (messageDomList.length > 0) {
          const lastMsgElement = messageDomList[messageDomList.length - 1] as HTMLElement;
          if (this.deviceService.isMobile()) {
            $('html').animate({
              scrollTop: lastMsgElement.offsetTop
            }, 10);
          } else {
            messageContainer.scrollTop = lastMsgElement.offsetTop;
          }
        }
      }
    }, 200);
  }

  private pollNewMessageUntilFinish(companyId, conversationId) {
    if (!conversationId || conversationId <= 0) {
      return;
    }
    this.pollingSubscription = timer(0, 5000)
      .pipe(concatMap(() => this.conversationService.fetchNewMessages(companyId, conversationId, this.lastActivity)))
      .subscribe((res) => {
        if (Array.isArray(res) && res.length > 0) {
          const existingMessageIds = this.messageList.map(msg => msg.id);
          const newMessages = res.filter((msg) => msg.id > 0 && existingMessageIds.indexOf(msg.id) < 0);
          this.messageList = this.messageList.concat(newMessages);
          this.conversation.messages = this.messageList;
          this.lastActivity = res[res.length - 1].sentTime;
          this.scrollToBottom();
          const newMessageUnread = res.filter(msg => msg.sender && msg.sender.company.id !== companyId);
          if (res.length > 0) {
            this.isReadLastMessage = false;
            this.newMessageAdded.emit({
              conversationId: this.conversation.id,
              status: newMessageUnread.length > 0 ? ShortMessageStatus.Warning : ShortMessageStatus.Normal,
              lastMessage: res[res.length - 1]
            });
          }
        }
      });
  }

  openCampaignDetails(campaignId, campaignInfluencerId, status) {
    const currentInfluencer = this.userService.getInfluencerInfo();
    // login as influencer
    if (currentInfluencer) {
      if (this.isActiveCampaign(status) && this.deviceService.isDesktop()) {
        return this.router.navigate(['dashboard', 'campaigns', campaignInfluencerId, 'active']);
      } else {
        return this.router.navigate(['dashboard', 'campaigns', campaignId, 'new']);
      }
    } else {
      return this.router.navigate(['campaigns', campaignId]);
    }
  }

  onReadLastMessage() {
    if (this.isReadLastMessage) {
      return;
    }
    const unReadMessages = this.messageList.filter(msg => ((msg.sender && msg.sender.company && msg.sender.company.id !== this.userService.getCompanyId()) || (msg.type === MessageType.InComming)));
    if (unReadMessages.length === 0) {
      this.isReadLastMessage = true;
      return;
    }
    this.afterReadLastMessage.emit({
      conversationId: this.conversation.id,
      lastMessageId: unReadMessages[unReadMessages.length - 1].id,
      callback: () => {
        this.isReadLastMessage = true;
      }
    });
  }

  getSenderName(msg) {
    if (!this.showActivityLog || !msg.sender) {
      return '';
    }
    return msg.sender.influencer ? `${msg.sender.influencer.name} - ` : msg.sender.user ? `${msg.sender.user.fullName} - ` : '';
  }

  getCampaignMessageStatus(mess) {
    if (mess.content.indexOf('Declined') >= 0 || mess.content.indexOf('Invited') >= 0
      || mess.content.indexOf('Accepted') >= 0 || mess.content.indexOf('Interested') >= 0) {
      return '';
    } else if (mess.messageType === ContactPermissionMessageType.requested) {
      return mess.status = 'requested';
    } else if (mess.messageType === ContactPermissionMessageType.accepted) {
      return mess.status = 'accepted';
    } else if (mess.messageType === ContactPermissionMessageType.rejected) {
      return mess.status = 'rejected';
    } else {
      return mess.status;
    }
  }

  get isMessageTooLong() {
    return this.inputMessageContent && this.inputMessageContent.length > InfluencerChatComponent.MAX_LENGTH_MESSAGE;
  }

  get shouldShowChatBox() {
    if (this.userService.isAdmin) {
      return true;
    }
    this.conversation.isHidden = false;
    return this.conversation;
  }

  numberCharacterOver(mess) {
    if (!mess) {
      return 0;
    }
    return mess.length - InfluencerChatComponent.MAX_LENGTH_MESSAGE;
  }

  isAcceptedPermission(conversation: Conversation) {
    return conversation.permission === ContactPermissionStatus.ACCEPTED;
  }

  isRejectedPermission(conversation: Conversation) {
    return conversation.permission === ContactPermissionStatus.REJECTED;
  }

  isNotRequestPermission(conversation: Conversation) {
    return !this.userService.isInfluencer && conversation.permission === ContactPermissionStatus.UNDEFINED;
  }

  isRequestedPermission(conversation: Conversation) {
    return !this.userService.isInfluencer && conversation.permission === ContactPermissionStatus.REQUESTED;
  }

  canSendMessage(conversation: Conversation) {
    if (!conversation) {
      return false;
    }
    // influencer can send message without permission
    if (this.userService.isInfluencer) {
      return true;
    }

    return conversation.permission === ContactPermissionStatus.ACCEPTED && !conversation.isHidden && conversation.isJoined;
  }

  sendPermissionRequest(conversation: Conversation) {
    this.sendMixpanel();
    if (this.userService.isAdmin) {
      this.modalService.openInCenter(this.adminEditConversationAlert);
      return;
    }
    const participant = conversation.participants.find(p => p.influencer);
    const influencerId = this.influencerId ? this.influencerId : participant ? participant.influencer.id : null;
    if (influencerId) {
      this.conversation.permission = ContactPermissionStatus.REQUESTED;
      this.buildContactPermissionRequest(conversation);
      this.pollNewMessageUntilFinish(this.userService.getCompanyId(), conversation.id);
      this.influencerService.requestPermission(this.userService.getCompanyId(), influencerId).subscribe((res) => {
        // do nothing
      }, (error) => {
        this.serverErrorMessage = this.getPermissionRequestErrorMessage(error.error);
        this.modalService.open(this.serverErrorNotification, null, true);
        // revert to previous status
        // revert request permission message
        this.conversation.permission = ContactPermissionStatus.UNDEFINED;
        conversation.messages = conversation.messages.filter(m => m.id !== this.REQUEST_PERMISSION_MESSAGE_ID);
        this.messageList = conversation.messages;
      });
    }
  }

  private getPermissionRequestErrorMessage(error) {
    const errorCode = error && error.error_code;

    if (errorCode === 'duplicate_request') {
      return this.translate.instant('inbox.duplicate_request_permission_error');
    }

    if (errorCode === 'invalid_company' || errorCode === 'invalid_influencer') {
      return this.translate.instant('inbox.invalid_request_permission_error');
    }

    return this.translate.instant('inbox.send_request_error');
  }

  getPermissionMessage(conversation: Conversation) {
    if (this.userService.isInfluencer) {
      return this.translate.instant('inbox.influencer_not_accept_permission_yet');
    }
    if (this.isRejectedPermission(conversation)) {
      return this.translate.instant('inbox.permission_rejected_message');
    }
    if (!this.isAcceptedPermission(conversation)) {
      return this.translate.instant('inbox.await_influencer_accept_permission');
    }
    return null;
  }

  needPermission(conversation: Conversation) {
    // influencer don't need permission to send message
    if (this.userService.isInfluencer) {
      return false;
    }
    return (conversation.permission === ContactPermissionStatus.REQUESTED
      || conversation.permission === ContactPermissionStatus.UNDEFINED
      || conversation.permission === ContactPermissionStatus.REJECTED) && conversation.isJoined;
  }

  closePopup() {
    this.modalService.close();
  }

  private isActiveCampaign(status) {
    if (!status) {
      return false;
    }
    return status.toLowerCase() === CommonConstant.CAMPAIGN_INFLUENCER_STATUS.ACTIVE.name.toLowerCase()
      || status.toLowerCase() === CommonConstant.CAMPAIGN_INFLUENCER_STATUS.ACCEPTED.name.toLowerCase()
      || status.toLowerCase() === CommonConstant.CAMPAIGN_INFLUENCER_STATUS.PUBLISHED.name.toLowerCase();
  }

  private buildContactPermissionRequest(conversation: any) {
    let recipient;
    this.userService.getCurrentUserInfo().subscribe(item => {
      recipient = item.data;
      const message = `Contact permission requested by: ${recipient.fullName}`;
      const requestMessage: Message = {
        id: this.REQUEST_PERMISSION_MESSAGE_ID,
        conservationId: this.conversation.id,
        activityAuthor: 'Achoo Influencer Platform',
        sender: {
          id: this.userService.getCompanyId(),
          name: recipient.fullName,
          company: null,
          influencer: null,
          lastSeenMessage: null,
          user: null
        },
        content: message,
        type: MessageType.Invitation,
        messageType: 'REQUESTED_PERMISSION',
        recipientName: recipient.fullName,
        sentTime: String(DateUtils.now().getTime())
      };
      conversation.messages.push(requestMessage);
      this.messageList = conversation.messages;
    });
  }

  private buildAutoReplyMessage(sentTime: number) {
    if (this.hasAutoReplyMessage) {
      return;
    }
    const systemUser = {
      id: null,
      name: null,
      company: null,
      influencer: null,
      lastSeenMessage: null,
      user: {
        image: 'assets/images/email_circle.svg',
        fullName: 'Achoo Influencer Platform'
      }
    };
    const autoReplyMessage: Message = {
      id: 1,
      conservationId: this.conversation.id,
      sender: systemUser,
      content: this.translate.instant('INFLUENCER.auto_reply_message'),
      type: MessageType.InComming,
      recipientName: null,
      sentTime: String(sentTime + 1)
    };
    this.hasAutoReplyMessage = true;
    this.messageList.push(autoReplyMessage);
  }

  private sendMixpanel() {
    let actionSource;
    let params = {
      companyId: this.currentUserInfo.company.id,
      action: UserActionType.MESSAGE
    };
    if (this.isLandingPageMode) {
      actionSource = ActionSource.CAMPAIGN_LANDING_PAGE;
    } else if (this.isCommunicationPage) {
      actionSource = ActionSource.COMMUNICATION_PAGE;
    } else {
      actionSource = ActionSource.ACTIVITY_TAB;
    }
    params = Object.assign({}, params, {
      source: actionSource
    });
    this.eventTracker.trackUserAction(UserActionType.ACTION, Page.CHAT, {
      type: this.userService.isInfluencer ? TargetType.INFLUENCER : TargetType.ADVERTISER,
      id: this.currentUserInfo.company.id,
      name: this.currentUserInfo.company.name,
    }, params, undefined, null);
  }

  getSenderAvatar(message) {
    if (!message || !message.sender) {
      return null;
    }
    if (message.sender.influencer) {
      return message.sender.influencer.profileImage;
    }
    if (message.sender.user) {
      return message.sender.user.image;
    }
    return null;
  }

  private hasChangedConvo(previous, current) {
    if (!previous && !current) {
      return false;
    }
    if (!previous && current) {
      return current.id > 0;
    }

    if (previous && !current) {
      return previous.id > 0;
    }
    return previous.id !== current.id && current.id > 0;
  }
}




// WEBPACK FOOTER //
// ./src/app/main/influencer-inbox/influencer-chat/influencer-chat.component.ts